import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, AlertTriangle, Hammer, Eye, CheckCircle, HelpCircle } from 'lucide-react';
import Button from '../components/ui/Button';

const SafetyTips: React.FC = () => {
  const safetyGuidelines = [
    {
      title: 'Før lån',
      icon: <Eye className="h-6 w-6" />,
      tips: [
        'Verificer altid værktøjsejerens identitet og vurderinger',
        'Tjek værktøjets fotos og beskrivelser grundigt',
        'Læs alle sikkerhedsinstruktioner før booking',
        'Sørg for at du forstår hvordan værktøjet betjenes',
      ],
    },
    {
      title: 'Under brug',
      icon: <Hammer className="h-6 w-6" />,
      tips: [
        'Bær passende sikkerhedsudstyr',
        'Følg producentens instruktioner',
        'Modificer eller ændr aldrig værktøjet',
        'Stop brugen hvis der opstår problemer',
      ],
    },
    {
      title: 'Efter brug',
      icon: <CheckCircle className="h-6 w-6" />,
      tips: [
        'Rengør værktøjet grundigt',
        'Rapporter eventuelle problemer med det samme',
        'Returner til tiden som aftalt',
        'Giv ærlig feedback',
      ],
    },
  ];

  const emergencySteps = [
    {
      title: 'Værktøjsfejl',
      description: 'Stop brugen med det samme og kontakt ejeren. Dokumenter problemet med fotos.',
    },
    {
      title: 'Skade',
      description: 'Søg lægehjælp hvis nødvendigt. Rapporter hændelsen til vores supportteam.',
    },
    {
      title: 'Materiel skade',
      description: 'Dokumenter skaden og kontakt både ejeren og vores supportteam.',
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-green-600 text-white">
        <div className="container mx-auto px-4 py-16 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl lg:text-6xl mb-6">
              Sikkerhed først
            </h1>
            <p className="text-xl text-green-100">
              Din sikkerhed er vores højeste prioritet. Følg disse retningslinjer for at sikre en 
              tryg og vellykket værktøjsdeling.
            </p>
          </div>
        </div>
      </div>

      {/* Safety Guidelines */}
      <div className="py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">Sikkerhedsretningslinjer</h2>
            <p className="mt-4 text-xl text-gray-600">
              Essentielle tips til sikker værktøjsbrug og deling
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {safetyGuidelines.map((section) => (
              <div key={section.title} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <div className="text-green-600 mb-4">{section.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{section.title}</h3>
                <ul className="space-y-3">
                  {section.tips.map((tip, index) => (
                    <li key={index} className="flex items-start">
                      <Shield className="h-5 w-5 text-green-500 mr-2 flex-shrink-0" />
                      <span className="text-gray-600">{tip}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Emergency Procedures */}
      <div className="bg-white py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-900">Nødprocedurer</h2>
            <p className="mt-4 text-xl text-gray-600">
              Ved hvad du skal gøre hvis noget går galt
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {emergencySteps.map((step) => (
              <div key={step.title} className="bg-red-50 rounded-lg p-6">
                <div className="flex items-center mb-4">
                  <AlertTriangle className="h-6 w-6 text-red-500 mr-2" />
                  <h3 className="text-lg font-bold text-gray-900">{step.title}</h3>
                </div>
                <p className="text-gray-600">{step.description}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <Link to="/help">
              <Button
                variant="outline"
                icon={<HelpCircle className="h-4 w-4 mr-2" />}
              >
                Besøg hjælpecenter
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Insurance Coverage */}
      <div className="py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
            <div className="max-w-3xl mx-auto text-center">
              <Shield className="h-12 w-12 text-green-600 mx-auto mb-6" />
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Forsikringsdækning</h2>
              <p className="text-gray-600 mb-8">
                Alle udlejninger gennem vores platform inkluderer grundlæggende forsikringsdækning. 
                Dette dækker utilsigtet skade op til 2.500 kr. Yderligere dækningsmuligheder er tilgængelige 
                for værktøj af høj værdi.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Report Issues */}
      <div className="bg-gray-900 text-white py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold mb-4">Rapporter sikkerhedsbekymringer</h2>
            <p className="text-gray-300 mb-8">
              Hvis du bemærker nogen sikkerhedsproblemer eller har bekymringer om et værktøj eller bruger, 
              bedes du rapportere det med det samme. Din sikkerhed er vores prioritet.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="mailto:safety@do-more.site?subject=Sikkerhedsproblem%20rapportering">
                <Button
                  variant="primary"
                  className="bg-red-600 hover:bg-red-700"
                  style={{height: '40px'}}
                >
                  Rapporter problem
                </Button>
              </a>
              <a href="mailto:support@do-more.site?subject=Support%20henvendelse">
                <button
                  className="inline-flex items-center justify-center font-medium rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-white px-4 py-2 text-sm border-2 border-white text-white hover:bg-white hover:text-gray-900"
                >
                  Kontakt support
                </button>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SafetyTips;